import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { useClub } from '../../app/club';
import { useRepo } from '../../app/services';
import { useAccessCodes, useAccessRules, useClubMutation, useDoors, useGroupMembers, useLocks } from '../../data/hooks';
import { Badge, Button, Dialog, EmptyState, ErrorState, Field, Loading, PageHeader, Panel, useToast, type Tone } from '../../components/ui';
import { computeScope, inScope } from '../../app/scope';
import { can } from '../../lib/roles';
import { CODE_KIND_LABEL, codeState, validateCodeWindow, type CodeState } from '../../lib/codes';
import { fmtWeekdayDateTime, MODEL_LABEL, toDateTimeInput } from '../../lib/format';
import type { AccessCode, CodeKind, Door } from '../../lib/types';
import type { CreatedCode } from '../../data/repository';
import { errorText } from '../../lib/errors';

const STATE_LABEL: Record<CodeState, { label: string; tone: Tone }> = {
  active: { label: 'Actief', tone: 'green' },
  scheduled: { label: 'Gepland', tone: 'yellow' },
  expired: { label: 'Verlopen', tone: 'neutral' },
  revoked: { label: 'Ingetrokken', tone: 'red' },
};

export function CodesPage() {
  const { orgId, role, managedGroupIds, readOnly } = useClub();
  const repo = useRepo();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const codes = useAccessCodes(orgId);
  const doors = useDoors(orgId);
  const locks = useLocks(orgId);
  const members = useGroupMembers(orgId);
  const rules = useAccessRules(orgId);
  const [filter, setFilter] = useState<'open' | 'all'>('open');
  const revoke = useClubMutation(orgId, (id: string) => repo.revokeAccessCode(id));
  const preset = params.get('nieuw');

  const qs = [codes, doors, locks, members, rules];
  if (qs.some((q) => q.isLoading)) return <Loading />;
  const err = qs.find((q) => q.error)?.error;
  if (err) return <ErrorState error={err} />;

  const scope = computeScope(role, managedGroupIds, members.data!, rules.data!);
  const keypadLockIds = new Set(locks.data!.filter((l) => l.model === 'D1_K').map((l) => l.id));
  const keypadDoors = doors.data!.filter((d) => d.lock_id && keypadLockIds.has(d.lock_id) && inScope(scope, 'doorIds', d.id));
  const doorByLock = new Map(doors.data!.filter((d) => d.lock_id).map((d) => [d.lock_id!, d]));
  const rows = codes
    .data!.filter((c) => inScope(scope, 'doorIds', doorByLock.get(c.lock_id)?.id ?? ''))
    .filter((c) => filter === 'all' || ['active', 'scheduled'].includes(codeState(c)))
    .sort((a, b) => b.valid_from.localeCompare(a.valid_from));

  const setWizard = (v: string | null) => {
    const p = new URLSearchParams(params);
    if (v) p.set('nieuw', v);
    else p.delete('nieuw');
    setParams(p, { replace: true });
  };

  return (
    <div className="stack-lg">
      <PageHeader
        title="Codes"
        intro={
          role === 'group_manager'
            ? 'Tijdelijke codes voor deuren van jouw groep, maximaal 7 dagen geldig.'
            : `Tijdelijke codes voor deuren met codeklavier (${MODEL_LABEL.D1_K}). Werken zonder internet; de ontvanger toetst de code en #.`
        }
        actions={
          can('code.create', role) &&
          !readOnly && (
            <Button variant="primary" onClick={() => setWizard('1')} disabled={keypadDoors.length === 0}>
              Code maken
            </Button>
          )
        }
      />
      <Panel>
        <div className="toolbar">
          <div className="tabs" role="tablist" aria-label="Welke codes" style={{ margin: 0, border: 0 }}>
            <button role="tab" aria-selected={filter === 'open'} onClick={() => setFilter('open')}>
              Actief en gepland
            </button>
            <button role="tab" aria-selected={filter === 'all'} onClick={() => setFilter('all')}>
              Alles, ook verlopen
            </button>
          </div>
        </div>
        {keypadDoors.length === 0 ? (
          <EmptyState title="Geen deuren met codeklavier">Codes kunnen alleen op een {MODEL_LABEL.D1_K}. Vraag je installateur naar de mogelijkheden.</EmptyState>
        ) : rows.length === 0 ? (
          <EmptyState title="Geen codes">Maak een code voor bijvoorbeeld een scheidsrechter of een gastteam.</EmptyState>
        ) : (
          <div className="table-wrap">
            <table className="data stack-mobile">
              <thead>
                <tr>
                  <th scope="col">Omschrijving</th>
                  <th scope="col">Deur</th>
                  <th scope="col">Geldig</th>
                  <th scope="col">Status</th>
                  <th scope="col">
                    <span className="sr-only">Acties</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => {
                  const st = codeState(c);
                  return (
                    <tr key={c.id}>
                      <td className="cell-main">
                        <span className="cell-title">{c.label}</span>
                        <span className="cell-sub">
                          {CODE_KIND_LABEL[c.kind]}
                          {c.recipient_name ? `, voor ${c.recipient_name}` : ''}
                        </span>
                      </td>
                      <td data-label="Deur">{doorByLock.get(c.lock_id)?.name}</td>
                      <td data-label="Geldig" className="num">
                        {fmtWeekdayDateTime(c.valid_from)}
                        <span className="cell-sub">tot {fmtWeekdayDateTime(c.valid_until)}</span>
                      </td>
                      <td>
                        <Badge tone={STATE_LABEL[st].tone}>{STATE_LABEL[st].label}</Badge>
                      </td>
                      <td>
                        {(st === 'active' || st === 'scheduled') && can('code.revoke', role) && !readOnly && (
                          <Button
                            size="sm"
                            disabled={revoke.isPending}
                            onClick={async () => {
                              await revoke.mutateAsync(c.id);
                              toast('Code ingetrokken. Wacht op sync met het slot.');
                            }}
                          >
                            Intrekken
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      {preset && <CodeWizard doors={keypadDoors} preset={preset} onClose={() => setWizard(null)} />}
    </div>
  );
}

function nextSaturday(hour: number): Date {
  const d = new Date();
  const add = (6 - d.getDay() + 7) % 7 || 7;
  d.setDate(d.getDate() + add);
  d.setHours(hour, 0, 0, 0);
  return d;
}

const recipientSchema = z.object({
  label: z.string().trim().min(3, 'Geef de code een omschrijving.'),
  contact: z.union([z.literal(''), z.string().trim().email(), z.string().trim().regex(/^[+0-9 ()-]{6,20}$/)]),
});

const STEPS = ['Deur', 'Wanneer', 'Ontvanger', 'Controleren'];

function CodeWizard({ doors, preset, onClose }: { doors: Door[]; preset: string; onClose: () => void }) {
  const { orgId, role, org } = useClub();
  const repo = useRepo();
  const isRef = preset === 'scheidsrechter';
  const [step, setStep] = useState(0);
  const [doorId, setDoorId] = useState(isRef ? doors.find((d) => /scheids/i.test(d.name))?.id ?? '' : '');
  const [kind, setKind] = useState<CodeKind>('offline_window');
  const [from, setFrom] = useState(toDateTimeInput(isRef ? nextSaturday(8) : new Date(Date.now() + 5 * 60_000)));
  const [until, setUntil] = useState(isRef ? toDateTimeInput(nextSaturday(18)) : '');
  const [label, setLabel] = useState(isRef ? 'Scheidsrechter' : '');
  const [recipient, setRecipient] = useState('');
  const [contact, setContact] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedCode | null>(null);
  const create = useClubMutation(orgId, (input: Parameters<typeof repo.createAccessCode>[1]) => repo.createAccessCode(orgId, input));
  const door = doors.find((d) => d.id === doorId);

  const validateStep = (): string | null => {
    if (step === 0 && !doorId) return 'Kies een deur.';
    if (step === 1) {
      const res = validateCodeWindow({ kind, validFrom: from, validUntil: until, role });
      if (!res.ok) return res.error;
    }
    if (step === 2) {
      const r = recipientSchema.safeParse({ label, contact });
      if (!r.success) return r.error.issues[0]?.path[0] === 'label' ? 'Geef de code een omschrijving.' : 'Vul een geldig e-mailadres of telefoonnummer in.';
    }
    return null;
  };

  const next = async () => {
    const e = validateStep();
    setError(e);
    if (e) return;
    if (step < 3) {
      setStep(step + 1);
      return;
    }
    const res = validateCodeWindow({ kind, validFrom: from, validUntil: until, role });
    if (!res.ok || !door?.lock_id) return;
    try {
      const c = await create.mutateAsync({
        lock_id: door.lock_id,
        kind,
        label: label.trim(),
        recipient_name: recipient.trim() || null,
        recipient_contact: contact.trim() || null,
        valid_from: res.validFrom,
        valid_until: res.validUntil,
      });
      setCreated(c);
    } catch (err) {
      setError(errorText(err, 'Code maken mislukt'));
    }
  };

  if (created) return <CodeCreated created={created} door={door!} orgName={org.name} onClose={onClose} />;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={isRef ? 'Code voor scheidsrechter' : 'Tijdelijke code maken'} description="Een tijdelijke code heeft altijd een eindtijd.">
      <ol className="steps" aria-label="Stappen">
        {STEPS.map((s, i) => (
          <li key={s} aria-current={i === step ? 'step' : undefined}>
            {i + 1}. {s}
          </li>
        ))}
      </ol>
      <form
        className="stack"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void next();
        }}
      >
        {step === 0 && (
          <Field label="Deur" hint={`Alleen deuren met codeklavier (${MODEL_LABEL.D1_K}).`}>
            {(p) => (
              <select {...p} className="input" value={doorId} onChange={(e) => setDoorId(e.target.value)} autoFocus>
                <option value="">Kies een deur</option>
                {doors.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            )}
          </Field>
        )}
        {step === 1 && (
          <>
            <fieldset className="field">
              <legend>Soort code</legend>
              <div className="choice-group">
                <label className="choice">
                  <input type="radio" name="kind" checked={kind === 'offline_window'} onChange={() => setKind('offline_window')} />
                  <div>
                    <strong>Tijdvenster</strong>
                    <span>Werkt onbeperkt vaak binnen het venster.</span>
                  </div>
                </label>
                <label className="choice">
                  <input type="radio" name="kind" checked={kind === 'offline_once'} onChange={() => setKind('offline_once')} />
                  <div>
                    <strong>Eenmalig</strong>
                    <span>Werkt één keer binnen het venster.</span>
                  </div>
                </label>
              </div>
            </fieldset>
            <div className="row" style={{ alignItems: 'flex-start' }}>
              <Field label="Vanaf">
                {(p) => <input {...p} className="input" type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} />}
              </Field>
              <Field label="Tot en met" hint={role === 'group_manager' ? 'Maximaal 7 dagen na de begintijd.' : undefined}>
                {(p) => <input {...p} className="input" type="datetime-local" value={until} onChange={(e) => setUntil(e.target.value)} required />}
              </Field>
            </div>
          </>
        )}
        {step === 2 && (
          <>
            <Field label="Omschrijving" hint='Bijvoorbeeld "Scheidsrechter JO17-1 za 2 nov"'>
              {(p) => <input {...p} className="input" value={label} onChange={(e) => setLabel(e.target.value)} autoFocus />}
            </Field>
            <Field label="Naam ontvanger (optioneel)">
              {(p) => <input {...p} className="input" value={recipient} onChange={(e) => setRecipient(e.target.value)} />}
            </Field>
            <Field label="E-mail of mobiel nummer (optioneel)" hint="Om de code na het maken te versturen.">
              {(p) => <input {...p} className="input" value={contact} onChange={(e) => setContact(e.target.value)} />}
            </Field>
          </>
        )}
        {step === 3 && (
          <dl className="kv">
            <dt>Deur</dt>
            <dd>{door?.name}</dd>
            <dt>Soort</dt>
            <dd>{CODE_KIND_LABEL[kind]}</dd>
            <dt>Geldig</dt>
            <dd>
              {fmtWeekdayDateTime(new Date(from).toISOString())} tot {until && fmtWeekdayDateTime(new Date(until).toISOString())}
            </dd>
            <dt>Omschrijving</dt>
            <dd>{label}</dd>
            <dt>Ontvanger</dt>
            <dd>{[recipient, contact].filter(Boolean).join(', ') || '—'}</dd>
          </dl>
        )}
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}
        <div className="dialog-actions">
          {step > 0 ? <Button onClick={() => { setError(null); setStep(step - 1); }}>Vorige</Button> : <Button onClick={onClose}>Annuleren</Button>}
          <Button variant="primary" type="submit" disabled={create.isPending}>
            {step < 3 ? 'Volgende' : 'Code maken'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function CodeCreated({ created, door, orgName, onClose }: { created: CreatedCode; door: Door; orgName: string; onClose: () => void }) {
  const c: AccessCode = created.code;
  const text = `Je toegangscode voor ${door.name} bij ${orgName}: ${created.secret}\nToets de code en daarna #.\nGeldig van ${fmtWeekdayDateTime(c.valid_from)} tot ${fmtWeekdayDateTime(c.valid_until)}.`;
  const contact = c.recipient_contact ?? '';
  const isEmail = contact.includes('@');
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title="Code gemaakt" description="Deze code zie je alleen nu. Verstuur hem direct naar de ontvanger.">
      <div className="stack">
        <div className="code-reveal" aria-label={`Code ${created.secret.split('').join(' ')}`}>
          {created.secret}
        </div>
        <p className="muted small">Toets de code en daarna #. {c.kind === 'offline_once' ? 'Werkt één keer.' : 'Werkt binnen het tijdvenster.'}</p>
        <div className="row">
          {contact && (
            <a
              className="btn btn-primary"
              href={
                isEmail
                  ? `mailto:${contact}?subject=${encodeURIComponent(`Toegangscode ${door.name}`)}&body=${encodeURIComponent(text)}`
                  : `sms:${contact}?&body=${encodeURIComponent(text)}`
              }
            >
              {isEmail ? 'Verstuur per e-mail' : 'Verstuur per sms'}
            </a>
          )}
          <Button onClick={() => void navigator.clipboard?.writeText(text)}>Kopieer bericht</Button>
        </div>
      </div>
      <div className="dialog-actions">
        <Button onClick={onClose}>Klaar</Button>
      </div>
    </Dialog>
  );
}
